import { AppContextProps } from '../../hooks/useApp'
import { Appliance, ApplianceData, ApplianceSubtype, ApplianceType } from './Appliance'
import { Device, ID, Port } from '../../types/models'
import { EnergyMeterAppliance } from './EnergyMeterAppliance'
import { PortConfig } from '../../types/ports'
import { PortControl } from '../ports/PortControl'
import { unreferencedPortControls } from '../ports/port-availability'
import { SolarAppliance } from './SolarAppliance'
import { SystemControl } from '../SystemControl'

/** Kinds of data-relaying sub-appliances the battery save flow can create. */
export type RelayedKind = 'EM_HAN' | 'SOLAR'

const RELAYED_DRIVERS: Record<RelayedKind, string> = {
  EM_HAN: 'EM_HAN_DRIVER_RELAYED',
  SOLAR: 'SPP_SOLAR_DRIVER_RELAYED',
}

/**
 * Check whether the device already relays this kind of data through an
 * existing appliance (HAN meter / PV production port with a *_RELAYED driver).
 * Used both to hide the "add relaying" toggles and to guard the save flow.
 */
export function hasRelayedAppliance(device: Device, appliances: Appliance[], kind: RelayedKind): boolean {
  const devicePortIDs = device.ports?.map(port => port.id) || []
  return appliances.some(appliance =>
    appliance.controls[0] !== undefined &&
    devicePortIDs.includes(appliance.portIDs[0]) &&
    appliance.getSubtype() === kind &&
    appliance.controls[0].getConfig('driver') === RELAYED_DRIVERS[kind])
}

/**
 * Build the relayed sub-appliance (HAN meter or PV production) that the
 * battery save flow creates alongside the battery when the relaying toggle is
 * on. Returns null when nothing must be created: the device already has a
 * relayed appliance of this kind (saving again used to create a duplicate) or
 * it has no free port left (used to crash the whole save). On success the
 * returned appliance is connected to a free port configured with the relayed
 * driver; the caller POSTs `data` and flashes the appliance's ports.
 */
export function buildRelayedSubAppliance(
  kind: RelayedKind,
  batteryPortControl: PortControl<Port, PortConfig>,
  appliances: Appliance[],
  control: SystemControl,
  app: AppContextProps,
): { subAppliance: Appliance, data: ApplianceData } | null {
  const device = batteryPortControl.deviceControl.device
  if (hasRelayedAppliance(device, appliances, kind)) return null

  const data: ApplianceData = {
    name: kind === 'EM_HAN' ? app.t('Meter through inverter') : app.t('PV production'),
    location_id: device.location_id as ID,
    type: (kind === 'EM_HAN' ? 'ENERGY_METER' : 'SOLAR_PANEL') as ApplianceType,
    ports: [],
    config: {},
  }
  const subAppliance = kind === 'EM_HAN'
    ? new EnergyMeterAppliance(data, control, app)
    : new SolarAppliance(data, control, app)
  subAppliance.connectControls(control)

  // Auto-allocation silently claims the port and rewrites its driver, so it
  // must both skip ports some appliance still references (its own delete
  // flash may not have confirmed yet, leaving `enabled` false while owned)
  // and stay away from raw ports an installer enabled by hand.
  const freePorts = subAppliance.filterSuitablePorts(
    unreferencedPortControls(
      control.getSuitablePorts(subAppliance, kind as ApplianceSubtype).filter(ctrl => !ctrl.getConfig('enabled')),
      appliances))
  const portCtrl = freePorts.find(ctrl => ctrl.deviceControl.device.hw_id === device.hw_id)
  if (!portCtrl) return null

  portCtrl.setConfig('enabled', true)
  portCtrl.setConfig('driver', RELAYED_DRIVERS[kind])
  if (kind === 'SOLAR') {
    portCtrl.setConfig('to_summer', false)
    portCtrl.setConfig('params', {})
  }
  subAppliance.portIDs = [portCtrl.port.id as ID]
  subAppliance.controls = [portCtrl]
  data.ports = [portCtrl.port.id as ID]

  return { subAppliance, data }
}
